<template>
  <div class="pi-shell">
    <base-dialog :show="!!error" title="An error occurred!" @close="handleError">
      <p>{{ error }}</p>
    </base-dialog>
    <transition name="toast">
      <div
        v-if="baseToast.message"
        class="pi-toast"
        :class="'is-' + (baseToast.type || 'info')"
      >
        <span class="dot"></span>
        <span>{{ baseToast.message }}</span>
      </div>
    </transition>

    <!-- ==================== Header ==================== -->
    <header class="pi-header">
      <div>
        <div class="eyebrow">Operations</div>
        <h1>Pay In</h1>
        <p class="subtitle">Live pay-in orders across your operators, banks, and clients. Refreshes in real time.</p>
      </div>
      <div class="header-actions">
        <button
          v-if="isClient"
          class="btn btn-primary"
          type="button"
          @click.stop="$router.push('/payin/create')"
        >
          <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2.2" class="btn-icon">
            <path d="M12 5v14M5 12h14" stroke-linecap="round" />
          </svg>
          Create Order
        </button>
        <button
          v-if="role === 'user'"
          class="btn btn-secondary"
          type="button"
          @click.stop="$router.push('/payin/verify-statement')"
        >
          <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2" class="btn-icon">
            <path d="M9 12l2 2 4-4" stroke-linecap="round" stroke-linejoin="round" />
            <circle cx="12" cy="12" r="10" />
          </svg>
          Verify Statement
        </button>
        <button
          v-if="role === 'subadmin'"
          class="btn btn-secondary"
          type="button"
          @click.stop="showUploadModal = true"
        >
          <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2" class="btn-icon">
            <path d="M12 3v14M6 9l6-6 6 6M4 21h16" stroke-linecap="round" stroke-linejoin="round" />
          </svg>
          Upload Statement
        </button>
        <button
          class="btn btn-secondary"
          type="button"
          :disabled="isExporting"
          @click.stop="exportData"
        >
          <svg v-if="!isExporting" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2" class="btn-icon">
            <path d="M12 21V7M6 13l6 6 6-6M4 3h16" stroke-linecap="round" stroke-linejoin="round" />
          </svg>
          <span v-else class="spinner-mini"></span>
          {{ isExporting ? 'Exporting…' : 'Export' }}
        </button>
        <button
          class="btn btn-icon-only"
          type="button"
          @click.stop="getPayInList"
          title="Refresh"
        >
          <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2">
            <path d="M4 4v5h.582m15.356 2A8.001 8.001 0 004.582 9m0 0H9m11 11v-5h-.581m0 0a8.003 8.003 0 01-15.357-2m15.357 2H15" stroke-linecap="round" stroke-linejoin="round" />
          </svg>
        </button>
      </div>
    </header>

    <!-- ==================== Filters bar ==================== -->
    <section class="pi-filters">
      <div class="filters-row">
        <!-- Scoped search (10082026): the column selector and the input are
             ONE control — pick where to search, then type. -->
        <div class="search-wrap">
          <div class="search-scope">
            <select v-model.trim="searchField" aria-label="Search column">
              <option value="utr">UTR</option>
              <option value="receiptId">Receipt ID</option>
              <option value="merchantOrderId">Order ID</option>
              <option value="refID">Ref ID</option>
              <option value="customerName">Customer Name</option>
              <option value="validatorUPIID">UPI ID</option>
              <option value="clientName">Client</option>
              <option value="bank">Bank</option>
              <option value="all">All fields</option>
            </select>
            <svg class="scope-chevron" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2">
              <path d="M6 9l6 6 6-6" stroke-linecap="round" stroke-linejoin="round" />
            </svg>
          </div>
          <div class="search-box">
            <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2" class="search-icon">
              <circle cx="11" cy="11" r="7" />
              <path d="M20 20l-3.5-3.5" stroke-linecap="round" />
            </svg>
            <input
              v-model.trim="searchKeyWord"
              type="text"
              placeholder="Search by receipt, order id, customer, UTR, UPI, bank…"
              class="search-input search-input-joined"
            />
            <button
              v-if="searchKeyWord"
              type="button"
              class="search-clear"
              @click="searchKeyWord = ''"
              title="Clear"
            >
              <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2">
                <path d="M6 18L18 6M6 6l12 12" stroke-linecap="round" />
              </svg>
            </button>
          </div>
        </div>

        <div class="chip-group">
          <button
            v-for="s in quickStatuses"
            :key="s.value"
            type="button"
            class="chip"
            :class="[
              { 'chip-active': paymentStatus === s.value },
              paymentStatus === s.value && s.tone ? `chip-active-${s.tone}` : ''
            ]"
            @click.stop="paymentStatus = s.value"
          >{{ s.label }}</button>
        </div>

        <!-- Time window (10082026): every list query is range-bounded.
             Payin is exempt from the 31-day span cap (20082026) — any
             custom range is allowed; the server enforces the same rule. -->
        <div class="select-wrap period-select">
          <select :value="period" @change="setPeriod($event.target.value)" aria-label="Time period">
            <option v-for="p in periodChips" :key="p.value" :value="p.value">{{ p.label }}</option>
          </select>
          <svg class="select-chevron" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2">
            <path d="M6 9l6 6 6-6" stroke-linecap="round" stroke-linejoin="round" />
          </svg>
        </div>

        <button
          type="button"
          class="btn btn-ghost"
          :class="{ 'is-active': showSearch }"
          @click.stop="toggleFilters"
        >
          <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2" class="btn-icon">
            <path d="M3 6h18M6 12h12M10 18h4" stroke-linecap="round" />
          </svg>
          {{ showSearch ? 'Hide filters' : 'More filters' }}
        </button>
      </div>

      <!-- Advanced filters panel -->
      <transition name="collapse">
        <div v-if="showSearch" class="filters-advanced">
          <div class="advanced-grid">
            <div class="field">
              <label>Amount</label>
              <div class="input-wrap">
                <span class="input-prefix">₹</span>
                <input v-model.trim="amount" type="number" placeholder="Any" />
              </div>
            </div>
            <div v-if="canFilterByBranch" class="field">
              <label>Bank</label>
              <div class="select-wrap">
                <select v-model.trim="validatorUsername">
                  <option value="">All banks</option>
                  <option v-for="(agent, i) in agents" :key="i" :value="agent.username">{{ agent.username }}</option>
                </select>
                <svg class="select-chevron" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2">
                  <path d="M6 9l6 6 6-6" stroke-linecap="round" stroke-linejoin="round" />
                </svg>
              </div>
            </div>
            <div v-if="canFilterByBranch" class="field">
              <label>Branch</label>
              <div class="select-wrap">
                <select v-model.trim="branchId">
                  <option value="">All branches</option>
                  <option v-for="b in branches" :key="b.id" :value="b.id">{{ b.name || b.username }}</option>
                </select>
                <svg class="select-chevron" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2">
                  <path d="M6 9l6 6 6-6" stroke-linecap="round" stroke-linejoin="round" />
                </svg>
              </div>
            </div>
            <div v-if="canFilterByBranch" class="field">
              <label>Client</label>
              <div class="select-wrap">
                <select v-model.trim="clientName">
                  <option value="">All clients</option>
                  <option v-for="c in clients" :key="c.clientName" :value="c.clientName">{{ c.clientName }}</option>
                </select>
                <svg class="select-chevron" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2">
                  <path d="M6 9l6 6 6-6" stroke-linecap="round" stroke-linejoin="round" />
                </svg>
              </div>
            </div>
            <div class="field">
              <label>UTR</label>
              <div class="select-wrap">
                <select v-model.trim="utrFilter">
                  <option value="">All orders</option>
                  <option value="with_utr">With UTR</option>
                  <option value="without_utr">Without UTR</option>
                </select>
                <svg class="select-chevron" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2">
                  <path d="M6 9l6 6 6-6" stroke-linecap="round" stroke-linejoin="round" />
                </svg>
              </div>
            </div>
            <div class="field">
              <label>Approval method</label>
              <div class="select-wrap">
                <select v-model.trim="approvalMethod">
                  <option value="">Any method</option>
                  <option value="manual">Manual</option>
                  <option value="auto_extension">Extension</option>
                  <option value="bank_statement">Bank Statement</option>
                </select>
                <svg class="select-chevron" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2">
                  <path d="M6 9l6 6 6-6" stroke-linecap="round" stroke-linejoin="round" />
                </svg>
              </div>
            </div>
            <div class="field">
              <label>From</label>
              <input type="date" v-model.trim="startDate" class="date-input" />
            </div>
            <div class="field">
              <label>To</label>
              <input type="date" v-model.trim="endDate" class="date-input" />
            </div>
          </div>
          <div class="advanced-foot">
            <button type="button" class="btn btn-ghost btn-danger" @click.stop="resetFilters">
              <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2" class="btn-icon">
                <path d="M3 6h18M8 6V4a2 2 0 012-2h4a2 2 0 012 2v2M19 6l-1 14a2 2 0 01-2 2H8a2 2 0 01-2-2L5 6" stroke-linecap="round" stroke-linejoin="round" />
              </svg>
              Reset filters
            </button>
          </div>
        </div>
      </transition>
    </section>

    <!-- ==================== Body ==================== -->
    <div v-if="isLoading" class="pi-loading">
      <div class="loading-spinner"></div>
      <div class="loading-label">Loading orders…</div>
    </div>

    <div v-else-if="!hasPayInList" class="empty-hero">
      <div class="empty-hero-icon">
        <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="1.6">
          <path d="M12 3v14M6 11l6 6 6-6M4 21h16" stroke-linecap="round" stroke-linejoin="round" />
        </svg>
      </div>
      <div class="empty-hero-title">No pay-in transactions found</div>
      <div class="empty-hero-sub">
        {{ hasAnyFilter ? 'Try adjusting your filters or clearing the date range.' : 'New orders will appear here live.' }}
      </div>
    </div>

    <div v-else class="pi-body">
      <div class="pi-grid">
        <pay-in-item
          v-for="(item, index) in filteredPayInList"
          :key="index"
          :item="item"
          :sl-no="paginatorInfo.firstItem + index"
          @refresh="getPayInList"
        ></pay-in-item>
      </div>
      <div class="pagination-wrap">
        <pagination :paginatorInfo="paginatorInfo" :setPage="setPage"></pagination>
      </div>
    </div>

    <!-- ==================== Upload statement modal (teleported) ==================== -->
    <teleport to="body">
      <transition name="modal-fade">
        <div v-if="showUploadModal" class="modal-backdrop" @click.self="showUploadModal = false">
          <div class="modal-card">
            <div class="modal-head">
              <div>
                <div class="modal-eyebrow">Bank statement</div>
                <div class="modal-title">Upload statement</div>
                <div class="modal-sub">Bulk-verify pay-in orders from a CSV export.</div>
              </div>
              <button type="button" class="modal-close" @click="showUploadModal = false">
                <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2.2">
                  <path d="M6 18L18 6M6 6l12 12" stroke-linecap="round" />
                </svg>
              </button>
            </div>
            <div class="modal-body">
              <div class="field">
                <label>Select bank</label>
                <div class="select-wrap">
                  <select v-model="selectedBank">
                    <option value="">— Select a bank —</option>
                    <option v-for="agent in agents" :key="agent.username" :value="agent.username">
                      {{ agent.name }} ({{ agent.username }})
                    </option>
                  </select>
                  <svg class="select-chevron" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2">
                    <path d="M6 9l6 6 6-6" stroke-linecap="round" stroke-linejoin="round" />
                  </svg>
                </div>
              </div>

              <div class="field">
                <label>Statement file</label>
                <div
                  @dragover.prevent="isDraggingPayin = true"
                  @dragleave.prevent="isDraggingPayin = false"
                  @drop.prevent="handleFileDropPayin"
                  :class="['drop-zone', { 'is-dragging': isDraggingPayin, 'has-file': uploadFile }]"
                  @click="$refs.payinFileInput.click()"
                >
                  <input ref="payinFileInput" type="file" @change="handleFile" accept=".csv" class="hidden-input" />
                  <div class="drop-icon">
                    <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="1.6">
                      <path d="M12 3v14M6 9l6-6 6 6M4 21h16" stroke-linecap="round" stroke-linejoin="round" />
                    </svg>
                  </div>
                  <div v-if="!uploadFile" class="drop-copy">
                    <div class="drop-title">Click to upload or drag & drop</div>
                    <div class="drop-sub">CSV file only</div>
                  </div>
                  <div v-else class="drop-file">
                    <div class="file-name">{{ uploadFile.name }}</div>
                    <div class="file-size">{{ formatFileSize(uploadFile.size) }}</div>
                  </div>
                </div>
              </div>
            </div>

            <div class="modal-foot">
              <button type="button" class="btn btn-secondary" @click="showUploadModal = false" :disabled="isUploading">Cancel</button>
              <button
                type="button"
                class="btn btn-primary"
                :disabled="!selectedBank || !uploadFile || isUploading"
                @click="uploadStatement"
              >
                <span v-if="!isUploading">Upload</span>
                <span v-else class="btn-loading">
                  <span class="spinner-mini"></span>
                  Uploading…
                </span>
              </button>
            </div>
          </div>
        </div>
      </transition>
    </teleport>
  </div>
</template>

<script>
import PayInItem from "../../components/payin/PayInItem.vue";
import http from "../../http-common";
import { getScopedSocket } from "../../utils/socket";
import Pagination from "../../components/shared/pagination.vue";
import { playNotificationSound } from "../../utils/notificationSound";

export default {
  name: "pay-in",
  components: {
    PayInItem,
    Pagination,
  },
  data() {
    return {
      isLoading: false,
      // Ties the spinner (and error surface) to the LATEST request — an
      // older request resolving late must not clear/flip either.
      loadSeq: 0,
      searchDebounceTimer: null,
      error: null,
      baseToast: {
        type: "",
        message: "",
      },
      showUploadModal: false,
      selectedBank: "",
      uploadFile: null,
      isUploading: false,
      isDraggingPayin: false,
      channel: null,
      showSearch: false,
      period: "today",
      searchField: "utr",
      periodChips: [
        { value: "today", label: "Today" },
        { value: "yesterday", label: "Yesterday" },
        { value: "2d", label: "Last 2 Days" },
        { value: "7d", label: "Last 7 Days" },
        { value: "30d", label: "Last 30 Days" },
        { value: "custom", label: "Custom" },
      ],

      searchKeyWord: "",
      amount: null,
      paymentStatus: "",
      validatorUsername: "",
      utrFilter: "",
      approvalMethod: "",
      // Vendor-only filters (admin / superadmin). Both are 'unfiltered'
      // when set to the empty string; backend ignores empty values.
      //   branchId:    SQL filter via validatorUsername IN (banks under
      //                that branch). Pay-in banks point at a branch via
      //                users.branch_id, so we send the branch user's id.
      //   clientName:  Plain equality on orders.clientName.
      branchId: "",
      clientName: "",
      // Dropdowns sourced from /users/subadmin (branches under vendor)
      // and /clients/getClientName (clients under vendor). Empty for
      // non-vendor roles since we never fetch.
      branches: [],
      clients: [],
      startDate: "",
      endDate: "",
      usersWithRole: [],
      socket: null,
      first: 10,
      page: 1,
      // Disables the Export button + swaps its label while the export fetch is
      // in flight, so a slow round-trip doesn't tempt double-clicks (each click
      // hits the API again and rebuilds a sheet).
      isExporting: false,
      quickStatuses: [
        { value: "",         label: "All",      tone: "neutral" },
        { value: "pending",  label: "Pending",  tone: "pending" },
        { value: "approved", label: "Approved", tone: "success" },
        { value: "expired",  label: "Expired",  tone: "danger"  },
        { value: "failed",   label: "Failed",   tone: "neutral" },
      ],
    };
  },
  watch: {
    page() {
      this.getPayInList();
    },
    showSearch(newVal) {
      if (newVal) {
        // IST date — see istDay(); local/UTC here re-created the
        // "Today shows yesterday" bug through the custom pickers.
        this.startDate = this.endDate = this.istDay(0);
      }
      if (!newVal) {
        this.startDate = this.endDate = "";
        if (this.period === "custom") {
          // Panel closed while on Custom → fall back to Today.
          this.period = "today";
        }
        this.getPayInList();
      }
    },
    // Typed inputs debounce: one request per pause, not one per keystroke.
    // (Pasting during the initial load used to fire a filtered request that
    // the slower unfiltered response then overwrote — see the store
    // action's request-seq guard for the other half of that fix.)
    searchKeyWord() {
      this.debouncedReload();
    },
    searchField() {
      if (String(this.searchKeyWord || "").trim()) this.debouncedReload();
    },
    amount() {
      this.debouncedReload();
    },
    paymentStatus() {
      this.page = 1;
      this.getPayInList();
    },
    validatorUsername() {
      this.page = 1;
      this.getPayInList();
    },
    approvalMethod() {
      this.page = 1;
      this.getPayInList();
    },
    branchId() {
      this.page = 1;
      this.getPayInList();
    },
    clientName() {
      this.page = 1;
      this.getPayInList();
    },
    startDate(newVal) {
      // Custom period: fetch only once both dates are set (periodRange
      // validates date order; getPayInList no-ops until valid — no span
      // cap on payin, 20082026).
      if (this.endDate || newVal == "") {
        this.page = 1;
        this.getPayInList();
      }
    },
    endDate(newVal) {
      if (this.startDate || newVal == "") {
        this.page = 1;
        this.getPayInList();
      }
    },
  },
  computed: {
    isAdmin() {
      return !!this.$store.getters.isAdmin;
    },
    // Vendor-level operators (admin / superadmin) oversee traffic across
    // multiple branches, so the Branch / Bank / Client filters are useful
    // for them. Matches how the payout list + bank list gate the same
    // trio of dropdowns — using `isAdmin` alone excluded superadmin.
    canFilterByBranch() {
      const r = this.$store.getters.role;
      return r === 'admin' || r === 'superadmin';
    },
    isClient() {
      return !!this.$store.getters.isClient;
    },
    paginatorInfo() {
      return this.$store.getters["payin/pagination"];
    },
    filteredPayInList() {
      let list = this.$store.getters["payin/payInList"];

      if (this.utrFilter === 'with_utr') {
        list = list.filter(item => item.customerUtr && item.customerUtr.trim() !== '');
      } else if (this.utrFilter === 'without_utr') {
        list = list.filter(item => !item.customerUtr || item.customerUtr.trim() === '');
      }

      return list;
    },
    hasPayInList() {
      return this.$store.getters["payin/hasPayInList"];
    },
    hasAnyFilter() {
      return !!(this.searchKeyWord || this.amount || this.paymentStatus || this.validatorUsername ||
                this.utrFilter || this.approvalMethod || this.branchId || this.clientName ||
                this.startDate || this.endDate);
    },
    role() {
      return this.$store.getters.role;
    },
    username() {
      return this.$store.getters.username;
    },
    agents() {
      return this.usersWithRole;
    },
    vendor() {
      return this.$store.getters.vendor;
    },
    userId() {
      return this.$store.getters.userId;
    },
  },
  created() {
    this.socket = getScopedSocket();

    this.socket.on("connect", () => {
      console.log("[IndiaPayU socket] connected. id=", this.socket.id, "role=", this.role, "userId=", this.userId);
    });

    this.socket.on("disconnect", () => {
      console.log("[IndiaPayU socket] disconnected");
    });

    // Diagnostic: log EVERY event this socket receives. Lets us tell whether
    // the issue is "events aren't arriving" (server doesn't emit / wrong
    // channel) vs "events arrive but our handler isn't firing" (subscribed
    // channel name doesn't match). Keep this on for now — the volume per
    // dashboard tab is low.
    this.socket.onAny((event, ...args) => {
      console.debug("[IndiaPayU socket] event:", event, args[0]);
    });

    this.getPayInList();
    this.getUsersWithRole();
    // Branch + Client filter dropdowns are vendor-only — fetch only
    // when the page is rendered for admin / superadmin so we don't
    // burn a request on roles that can't use them.
    if (this.canFilterByBranch) {
      this.getBranches();
      this.getClients();
    }

    // Scope socket subscriptions to the narrowest room the user is entitled to.
    // Without this, a branch (subadmin) subscribed to the vendor-wide room and
    // saw real-time events for every other branch's orders under the same vendor.
    //
    // Defensive fallback: if a subadmin is on a legacy JWT that doesn't carry
    // `id` (issued before we added it to the login payload), subscribing to
    // `-branch-undefined` would drop every event. Until the user re-logs we
    // fall back to the vendor-wide room so they at least see new orders.
    let suffix;
    if (this.role == "admin" || this.role == "superadmin") {
      // Vendor-level operators (admin + superadmin) subscribe to the
      // vendor-wide room. Previously superadmin fell through to the else
      // branch below, landing on `-${username}` which no backend emit
      // targets — so superadmins received zero realtime updates on the
      // payin list and saw stale data until they manually refreshed.
      suffix = "";
    } else if (this.role == "subadmin") {
      if (!this.userId) {
        console.warn(
          "[IndiaPayU] subadmin missing userId — likely a stale JWT from before id was added. " +
          "Falling back to vendor-wide socket room. Log out and back in to scope to your branch only."
        );
        suffix = "";
      } else {
        suffix = `-branch-${this.userId}`;
      }
    } else if (this.role == "client") {
      // Clients listen on the dedicated `-client-${clientName}` room so
      // backend fan-out reaches them only for their own orders. clientName
      // comes from the JWT's `name` claim (== users.name == clientName).
      // If the JWT is legacy and lacks `name`, fall back to vendor-wide so
      // the user at least sees something (privacy-degraded but functional).
      const clientName = this.$store.getters.name;
      if (!clientName) {
        console.warn("[IndiaPayU] client missing name — legacy JWT? Falling back to vendor-wide room.");
        suffix = "";
      } else {
        suffix = `-client-${clientName}`;
      }
    } else {
      suffix = `-${this.username}`;
    }

    console.log(
      "[IndiaPayU socket] subscribing for role=", this.role,
      "vendor=", this.vendor, "userId=", this.userId, "suffix=", suffix,
      "channels=", [
        `${this.vendor}-order-created${suffix}`,
        `${this.vendor}-order-amount-created${suffix}`,
        `${this.vendor}-order-update-status-and-trnx${suffix}`,
      ]
    );

    this.socket.on(`${this.vendor}-order-created${suffix}`, (data) => {
      this.addOrderToPayInList(data);
      this.playSound('payin');
    });
    this.socket.on(`${this.vendor}-order-amount-created${suffix}`, (data) => {
      this.updateAmountAndStatus(data);
    });
    this.socket.on(`${this.vendor}-customer-upi-updated${suffix}`, (data) => {
      this.updateCustomerUpiID(data);
    });
    this.socket.on(`${this.vendor}-customer-utr-updated${suffix}`, (data) => {
      this.updateCustomerUtr(data);
    });
    this.socket.on(`${this.vendor}-customer-screenshot-updated${suffix}`, (data) => {
      this.updateCustomerScreenshot(data);
    });
    this.socket.on(`${this.vendor}-order-update-status-and-trnx${suffix}`, (data) => {
      this.updateStatusAndTransaction(data);
    });
    // Catch-all: any mutation in the backend now also fires `order-updated`
    // with the full row. Merge it into the matching list entry so fields
    // the specific events above don't carry (approvedBy, approval_method,
    // updatedAt, ...) still update in realtime.
    this.socket.on(`${this.vendor}-order-updated${suffix}`, (data) => {
      this.handleOrderUpdated(data);
    });
    this.socket.on("order-approved-auto", (data) => {
      this.autoOrderApproved(data);
    });
  },
  methods: {
    playSound(type) {
      playNotificationSound(type);
    },
    formatFileSize(bytes) {
      if (!bytes) return "";
      const kb = bytes / 1024;
      if (kb < 1024) return `${kb.toFixed(1)} KB`;
      return `${(kb / 1024).toFixed(2)} MB`;
    },
    toggleFilters() {
      this.showSearch = !this.showSearch;
    },
    resetFilters() {
      this.amount = null;
      this.paymentStatus = "";
      this.validatorUsername = "";
      this.utrFilter = "";
      this.approvalMethod = "";
      this.branchId = "";
      this.clientName = "";
      this.startDate = "";
      this.endDate = "";
      this.page = 1;
      this.getPayInList();
    },
    handleFile(e) {
      this.uploadFile = e.target.files[0];
    },
    handleFileDropPayin(event) {
      this.isDraggingPayin = false;
      const files = event.dataTransfer.files;
      if (files.length > 0) {
        this.uploadFile = files[0];
      }
    },
    async uploadStatement() {
      if (!this.selectedBank || !this.uploadFile) return;

      this.isUploading = true;
      const formData = new FormData();
      formData.append('bankStatement', this.uploadFile);
      formData.append('bankUsername', this.selectedBank);

      try {
        const response = await http.post('/orders/upload-bank-statement-by-branch', formData, {
          headers: { 'Content-Type': 'multipart/form-data' }
        });
        if (response.data.success) {
          this.baseToast = { type: 'success', message: `Uploaded: ${response.data.report.summary.totalVerified} verified` };
          this.showUploadModal = false;
          this.selectedBank = '';
          this.uploadFile = null;
          this.getPayInList();
        } else {
          this.baseToast = { type: 'error', message: response.data.message || 'Upload failed' };
        }
      } catch (error) {
        this.baseToast = { type: 'error', message: error.response?.data?.message || 'Upload failed' };
      } finally {
        this.isUploading = false;
      }
    },
    setPage(page) {
      this.page = page;
    },
    // Populate the Branch filter dropdown (vendor-only). Backend
    // /users/subadmin auto-scopes to the requester's vendor; superadmin
    // gets all branches.
    async getBranches() {
      try {
        const response = await http.get("/users/subadmin");
        if (response.status === 200) {
          this.branches = response.data || [];
        }
      } catch (error) {
        console.log(error);
      }
    },
    // Populate the Client filter dropdown (vendor-only). /clients/getClientName
    // is vendor-scoped server-side (see controllers/clients/getClients.js:28).
    // Pull a generous first page — we'd need pagination only for vendors with
    // hundreds of clients, which isn't the norm.
    async getClients() {
      try {
        const response = await http.get("/clients/getClientName?first=500&page=1");
        if (response.status === 200) {
          // Endpoint returns { clients: [...], pagination }. Be tolerant
          // of either shape so a future change to the response envelope
          // doesn't silently break the dropdown.
          const payload = response.data || {};
          this.clients = payload.clients || payload.data || [];
        }
      } catch (error) {
        console.log(error);
      }
    },
    async getUsersWithRole() {
      try {
        const response = await http.get("/users/user");
        if (response.status == 200) {
          this.usersWithRole = response.data;
        }
      } catch (error) {
        console.log(error);
      }
    },
    updateStatusAndTransaction(data) {
      if (!this.showSearch) {
        this.$store.commit("payin/updateStatusAndTransaction", data);
      }
    },
    updateAmountAndStatus(data) {
      if (!this.showSearch) {
        this.$store.commit("payin/updateAmountAndStatus", data);
      }
    },
    updateCustomerUpiID(data) {
      if (!this.showSearch) {
        this.$store.commit("payin/updateCustomerUpiID", data);
      }
    },
    updateCustomerUtr(data) {
      if (!this.showSearch) {
        this.$store.commit("payin/updateCustomerUtr", data);
      }
    },
    updateCustomerScreenshot(data) {
      if (!this.showSearch) {
        this.$store.commit("payin/updateCustomerUtrAndAttachment", data);
      }
    },
    autoOrderApproved(data) {
      this.$store.commit("payin/setPaymentStatusAndUtr", data);
    },
    // Receives the post-mutation full row from emitOrderUpdated. Only
    // applies when the row is a payin and is already in the visible list;
    // mergeOrderRow ignores rows that aren't present (new orders come via
    // the dedicated order-created handler which already gates on filters).
    handleOrderUpdated(data) {
      if (!data || data.type !== 'payin') return;
      this.$store.commit("payin/mergeOrderRow", data);
    },
    // Returns true if `order` would survive every active filter. When no
    // filter is set this is trivially true, so unfiltered views keep their
    // realtime push behaviour. Used to gate new socket-pushed orders so a
    // user looking at "approved" doesn't suddenly see a "pending" order
    // pop in just because it was created seconds ago.
    matchesActiveFilters(order) {
      if (!order) return false;
      // Client scope guard. When a client falls back to the vendor-wide
      // socket room (legacy JWT without `name`), socket-pushed new orders
      // for OTHER clients under the same vendor would otherwise unshift
      // into this client's list. The list endpoint is server-filtered by
      // clientName, but realtime pushes aren't — enforce it here.
      if (this.role === 'client') {
        const myName = this.$store.getters.name;
        // Legacy JWT (no name claim) → drop every realtime push to be safe.
        // The operator must re-login to restore realtime; until then the
        // list refreshes only on manual reload.
        if (!myName) return false;
        if (order.clientName !== myName) return false;
      }
      if (this.paymentStatus && order.paymentStatus !== this.paymentStatus) return false;
      if (this.amount && Number(order.amount) !== Number(this.amount)) return false;
      if (this.validatorUsername && order.validatorUsername !== this.validatorUsername) return false;
      if (this.approvalMethod && (order.approval_method || '') !== this.approvalMethod) return false;
      // Branch filter is keyed off branchId (the subadmin's user.id). The
      // realtime row only carries validatorUsername / clientName; we'd
      // need to know which branch the validator belongs to to match it
      // properly. Cheaper to just check against the loaded branches list
      // — match on the branch's own username OR any of its banks'
      // usernames listed in `branches[*].sub_users`. Since /users/subadmin
      // doesn't expose the banks under each branch by default, we fall
      // back to dropping the realtime row when branchId is active so the
      // user never sees a non-matching order pop in. Next manual list
      // fetch will repopulate correctly.
      if (this.branchId) return false;
      if (this.clientName && order.clientName !== this.clientName) return false;
      if (this.utrFilter === 'with_utr' && !order.customerUtr) return false;
      if (this.utrFilter === 'without_utr' && order.customerUtr) return false;
      if (this.searchKeyWord) {
        const needle = String(this.searchKeyWord).toLowerCase();
        const haystack = [
          order.customerName, order.merchantOrderId, order.receiptId,
          order.transactionID, order.customerUPIID, order.customerUtr,
          order.validatorUsername, order.refID, order.clientName,
        ].map((v) => String(v || '').toLowerCase()).join(' ');
        if (!haystack.includes(needle)) return false;
      }
      return true;
    },
    addOrderToPayInList(data) {
      // Only push when we're on page 1 (older pages would shift) AND the
      // incoming order matches every active filter — otherwise the user's
      // filtered view gets polluted with non-matching rows.
      if (this.page == 1 && data.type == "payin" && this.matchesActiveFilters(data)) {
        this.$store.commit("payin/addOrderToPayInList", data);
      }
    },
    async getPayInList() {
      const range = this.periodRange();
      if (!range) return; // custom period awaiting valid dates
      const params = {
        type: "payin",
        searchKeyWord: this.searchKeyWord,
        amount: this.amount,
        paymentStatus: this.paymentStatus,
        validatorUsername: this.validatorUsername,
        approval_method: this.approvalMethod,
        // Vendor-only filters — backend ignores them for other roles
        // (subadmin/client already auto-scope). Sending the keys with
        // empty strings is fine: the store action strips empties.
        branchId: this.branchId,
        clientName: this.clientName,
        startDate: range.startDate,
        endDate: range.endDate,
        searchField: this.searchField,
        first: this.first,
        page: this.page,
      };
      const seq = ++this.loadSeq;
      this.isLoading = true;
      try {
        await this.$store.dispatch("payin/getPayInList", params);
      } catch (error) {
        if (seq === this.loadSeq) this.error = error.message || "Something failed!";
      } finally {
        if (seq === this.loadSeq) this.isLoading = false;
      }
    },
    setPeriod(p) {
      this.period = p;
      if (p === "custom") {
        // Open the advanced panel so the date pickers are visible; fetch
        // happens when the operator picks valid dates (date watchers).
        this.showSearch = true;
        return;
      }
      if (this.page !== 1) this.page = 1;
      else this.getPayInList();
    },
    // The server enforces the same rules — this is UX-side validation only.
    // IST calendar date for now+offset days. NEVER new Date().toISOString()
    // (that's the browser clock converted to UTC): the backend interprets
    // these strings in IST, so a viewer west of IST — or any machine
    // before 05:30 IST — would compute yesterday's date and see
    // yesterday's orders under "Today". Intl gives us the IST date
    // directly; en-CA formats as YYYY-MM-DD. IST has no DST, so the
    // ms-offset day arithmetic is safe.
    istDay(offset = 0) {
      return new Intl.DateTimeFormat("en-CA", {
        timeZone: "Asia/Kolkata",
        year: "numeric",
        month: "2-digit",
        day: "2-digit",
      }).format(new Date(Date.now() + offset * 86400000));
    },
    periodRange() {
      const day = (offset) => this.istDay(offset);
      if (this.period === "today") return { startDate: day(0), endDate: day(0) };
      if (this.period === "yesterday") return { startDate: day(-1), endDate: day(-1) };
      if (this.period === "2d") return { startDate: day(-1), endDate: day(0) };
      if (this.period === "7d") return { startDate: day(-6), endDate: day(0) };
      if (this.period === "30d") return { startDate: day(-29), endDate: day(0) };
      // custom — no max-span cap on the payin list (20082026): the server
      // exempts type=payin from the 31-day limit (orders table is held to
      // ~60 days by the archival worker, so wide ranges stay bounded).
      if (!this.startDate || !this.endDate) return null; // wait for both
      const span = (new Date(this.endDate) - new Date(this.startDate)) / 86400000;
      if (span < 0) { this.error = "End date is before start date."; return null; }
      return { startDate: this.startDate, endDate: this.endDate };
    },
    debouncedReload() {
      clearTimeout(this.searchDebounceTimer);
      this.searchDebounceTimer = setTimeout(() => {
        // page=1 assignment triggers the page watcher's fetch; fetching
        // here too would double every search request when paginated deep.
        if (this.page !== 1) {
          this.page = 1;
        } else {
          this.getPayInList();
        }
      }, 300);
    },
    async exportData() {
      // Fetch every row that matches the current filters (capped at 5k) and
      // build the XLSX from THAT — not from `payInList`, which is just the
      // current paginated slice. Hitting "Export" on page 3 used to dump only
      // page 3's rows; this rewrite fixes that.
      if (this.isExporting) return;
      // Hard gate: both date bounds must be set. Without them an export could
      // sweep up the entire table (capped at 5k) and quietly truncate. Forcing
      // a range also keeps the resulting file small enough to open.
      if (!this.startDate || !this.endDate) {
        this.$swal.fire({
          icon: 'warning',
          title: 'Date range required',
          text: 'Please pick both "From" and "To" dates before exporting.',
        });
        return;
      }
      this.isExporting = true;
      try {
        const EXPORT_CAP = 5000;
        const qs = new URLSearchParams();
        qs.set('type', 'payin');
        if (this.role === 'user') qs.set('validatorUsername', this.username);
        if (this.searchKeyWord) qs.set('searchKeyWord', this.searchKeyWord);
        if (this.amount) qs.set('amount', this.amount);
        if (this.paymentStatus) qs.set('paymentStatus', this.paymentStatus);
        if (this.validatorUsername) qs.set('validatorUsername', this.validatorUsername);
        if (this.approvalMethod) qs.set('approval_method', this.approvalMethod);
        if (this.branchId) qs.set('branchId', this.branchId);
        if (this.clientName) qs.set('clientName', this.clientName);
        if (this.startDate && this.endDate) {
          qs.set('startDate', this.startDate);
          qs.set('endDate', this.endDate);
        }
        qs.set('first', String(EXPORT_CAP));
        qs.set('page', '1');

        const response = await http.get(`/orders?${qs.toString()}`);
        if (response.status !== 200) throw new Error('Server returned ' + response.status);
        let rows = (response.data && response.data.data) || [];

        // utrFilter is a client-side toggle (the backend has no equivalent
        // query param), so mirror filteredPayInList's narrowing here.
        if (this.utrFilter === 'with_utr') rows = rows.filter(r => r.customerUtr);
        else if (this.utrFilter === 'without_utr') rows = rows.filter(r => !r.customerUtr);

        if (rows.length === 0) {
          this.$swal.fire({
            icon: 'info',
            title: 'Nothing to export',
            text: 'No Pay-In orders match the current filters.',
          });
          return;
        }

        // DECIMAL columns come back from mysql2 as strings. Coerce to Number
        // so Excel treats the column as numeric (SUM / sort work).
        const num = (v) => {
          const n = Number(v);
          return Number.isFinite(n) ? n : '';
        };
        // createdAt / updatedAt are stored as raw IST strings (see CLAUDE.md)
        // but JSON-serialized with a misleading "Z" suffix. Strip the T/Z and
        // ms so Excel shows the IST clock time the operator sees in-app.
        const fmt = (v) => String(v || '').replace('T', ' ').replace(/\.\d+Z?$/, '').replace(/Z$/, '');
        const approvedAt = (item) => item.paymentStatus === 'approved' ? fmt(item.updatedAt) : '';

        // Column-level scoping per role. Row scoping is already enforced by
        // the backend; this trims columns so we don't leak vendor / branch /
        // operator info to roles that shouldn't see it.
        //   - client: hide Branch + Approved By Username (operator internals).
        //   - user (bank): hide Vendor + Client Name (above their pay-grade).
        //   - subadmin (branch): hide Client Name.
        //   - admin / superadmin: see everything.
        const HIDDEN_COLS_BY_ROLE = {
          client: new Set(['Branch', 'Approved By Username']),
          user: new Set(['Vendor', 'Client Name']),
          subadmin: new Set(['Client Name']),
        };
        const hidden = HIDDEN_COLS_BY_ROLE[this.role];

        const data = rows.map(item => ({
          // Split out into two columns: Branch is the branch the bank rolls up
          // to (or empty for legacy rows); Bank is the bank user that handled
          // the pay-in. The old single "Branch User Name" column conflated
          // them via `branch_username || validatorUsername`, which gave the
          // wrong label half the time.
          'Branch': item.branch_username || item.branch_name || '',
          'Bank': item.validatorUsername || '',
          'Receipt ID': item.receiptId || '',
          'Order ID': item.merchantOrderId || '',
          'Vendor': item.vendor || '',
          'Client Name': item.clientName || '',
          'Type': item.type || '',
          // Customer detail is restricted to Name only. Customer UTR, UPI,
          // mobile, IP, etc. are intentionally NOT included in the export.
          'Customer Name': item.customerName || '',
          'Amount': num(item.amount),
          'Payment Status': item.paymentStatus || '',
          // Mode = the rail the client used (UPI / bank); matches what the
          // order card labels as "Payment Method". paymentMethod (next col)
          // is the validator's bank-automation profile — different thing,
          // useful for ops debugging.
          'Mode': item.mode || '',
          'Payment Method': item.paymentMethod || '',
          'Transaction ID': item.transactionID || '',
          'Approved By Username': item.approvedByUsername || '',
          'Created At': fmt(item.createdAt),
          'Approved At': approvedAt(item),
        }));

        // Import the self-contained UMD bundle, not the package's main entry.
        // Under Vue CLI 4 / webpack 4 the `await import('xlsx')` path can
        // resolve to a module wrapper that throws "Cannot read properties of
        // undefined (reading 'call')" at runtime; the UMD bundle loads
        // cleanly. The `.default || mod` unwrap handles both the
        // `__esModule`-flagged and bare UMD cases.
        // Apply role-based column hiding right before serializing.
        const scoped = hidden
          ? data.map(row => {
              const out = {};
              for (const k of Object.keys(row)) {
                if (!hidden.has(k)) out[k] = row[k];
              }
              return out;
            })
          : data;

        const xlsxMod = await import('xlsx/dist/xlsx.full.min.js');
        const XLSX = xlsxMod.default || xlsxMod;
        const ws = XLSX.utils.json_to_sheet(scoped);
        const wb = XLSX.utils.book_new();
        XLSX.utils.book_append_sheet(wb, ws, 'PayIn');
        // Filename uses IST date so it matches what the operator sees in the
        // app (DATETIME columns are stored IST per CLAUDE.md). UTC was off by
        // up to a day near IST midnight.
        const istDate = new Date().toLocaleDateString('en-CA', { timeZone: 'Asia/Kolkata' });
        XLSX.writeFile(wb, `payin-export-${istDate}.xlsx`);

        if (rows.length >= EXPORT_CAP) {
          this.$swal.fire({
            icon: 'warning',
            title: 'Export truncated',
            text: `Only the first ${EXPORT_CAP} rows were exported. Narrow your filters to capture the rest.`,
          });
        }
      } catch (error) {
        // Server (axios) errors carry the API message under .response.data.message;
        // generic Errors carry .message; everything else stringifies. Cover all.
        const msg =
          (error && error.response && error.response.data && error.response.data.message) ||
          (error && error.message) ||
          (error ? String(error) : 'unknown error');
        this.error = 'Export failed: ' + msg;
      } finally {
        this.isExporting = false;
      }
    },
    handleError() {
      this.error = null;
    },
    searchBtnClicked() {
      this.showSearch = true;
    },
    cancelBtnClicked() {
      this.showSearch = false;
      this.searchKeyWord = "";
      this.amount = null;
      this.paymentStatus = "";
      this.validatorUsername = "";
      this.startDate = "";
      this.endDate = "";
    },
  },
  beforeUnmount() {
    clearTimeout(this.searchDebounceTimer);
    if (this.socket) {
      this.socket.disconnect();
    }
  },
};
</script>

<style scoped>
/* ==================== Shell ==================== */
.pi-shell {
  height: 100%;
  display: flex;
  flex-direction: column;
  padding: 24px;
  gap: 20px;
  background: linear-gradient(180deg, #fbfaf7 0%, #f4f0e5 100%);
  overflow-y: auto;
}

/* ==================== Header ==================== */
.pi-header {
  display: flex;
  align-items: flex-end;
  justify-content: space-between;
  gap: 16px;
  flex-wrap: wrap;
}
.eyebrow {
  font-size: 11px;
  letter-spacing: 0.12em;
  text-transform: uppercase;
  color: #8a8371;
  font-weight: 600;
  margin-bottom: 4px;
}
.pi-header h1 {
  font-size: 28px;
  font-weight: 700;
  color: #1b2a47;
  line-height: 1.1;
  margin: 0;
  letter-spacing: -0.01em;
}
.subtitle {
  font-size: 13px;
  color: #64748b;
  margin: 4px 0 0;
  max-width: 640px;
  line-height: 1.5;
}
.header-actions {
  display: flex;
  gap: 8px;
  align-items: center;
  flex-wrap: wrap;
}

/* ==================== Buttons ==================== */
.btn {
  display: inline-flex;
  align-items: center;
  gap: 8px;
  padding: 10px 16px;
  border-radius: 10px;
  font-weight: 600;
  font-size: 13px;
  cursor: pointer;
  border: 1px solid transparent;
  transition: all 0.15s;
  outline: none;
  font-family: inherit;
  text-decoration: none;
}
.btn:disabled { opacity: 0.55; cursor: not-allowed; }
.btn-primary {
  background: linear-gradient(135deg, #1B2A47 0%, #2C3E63 100%);
  color: #fff;
  box-shadow: 0 6px 16px -6px rgba(27, 42, 71, 0.45);
}
.btn-primary:hover:not(:disabled) {
  transform: translateY(-1px);
  box-shadow: 0 10px 24px -8px rgba(27, 42, 71, 0.55);
}
.btn-secondary {
  background: #fff;
  color: #475569;
  border-color: #ede9df;
}
.btn-secondary:hover:not(:disabled) { background: #fbfaf7; border-color: #cbd5e1; color: #1b2a47; }
.btn-ghost {
  background: transparent;
  color: #64748b;
  border-color: #ede9df;
}
.btn-ghost:hover:not(:disabled) { background: #f4f0e5; color: #1b2a47; }
.btn-ghost.is-active { background: #eef2f7; color: #1B2A47; border-color: #cbd5e1; }
.btn-ghost.btn-danger { color: #b91c1c; border-color: #fecaca; }
.btn-ghost.btn-danger:hover { background: #fef2f2; color: #991b1b; }
.btn-icon-only {
  padding: 0;
  width: 40px; height: 40px;
  background: #fff;
  color: #475569;
  border-color: #ede9df;
  justify-content: center;
}
.btn-icon-only:hover:not(:disabled) { background: #fbfaf7; color: #1b2a47; border-color: #cbd5e1; }
.btn-icon-only svg { width: 16px; height: 16px; }
.btn-icon { width: 15px; height: 15px; }
.btn-loading { display: inline-flex; align-items: center; gap: 8px; }

.spinner-mini {
  width: 14px; height: 14px;
  border-radius: 999px;
  border: 2px solid #cbd5e1;
  border-top-color: #1B2A47;
  animation: spin 0.7s linear infinite;
  display: inline-block;
}
.btn-primary .spinner-mini { border-color: rgba(255,255,255,0.35); border-top-color: #fff; }

/* ==================== Filters ==================== */
.pi-filters {
  background: #fff;
  border: 1px solid #ede9df;
  border-radius: 14px;
  padding: 12px 14px;
  display: flex;
  flex-direction: column;
  gap: 12px;
}
.filters-row {
  display: flex;
  flex-wrap: wrap;
  gap: 10px;
  align-items: center;
}
/* Joined scoped-search control (10082026): the WRAPPER owns the chrome —
   one border, one background, one radius, one hover/focus ring — so the
   scope segment and the input can never look like two controls. The
   input's own border/background/shadow are stripped when joined. */
.search-wrap {
  position: relative;
  flex: 1;
  min-width: 260px;
  display: flex;
  align-items: stretch;
  border: 1px solid #e2e8f0;
  border-radius: 10px;
  background: #fbfaf7;
  transition: border-color 0.15s, background 0.15s, box-shadow 0.15s;
}
.search-wrap:hover { border-color: #cbd5e1; }
.search-wrap:focus-within {
  border-color: #1B2A47;
  background: #fff;
  box-shadow: 0 0 0 4px rgba(27, 42, 71, 0.08);
}
.search-scope {
  display: flex;
  align-items: center;
  position: relative;
  border-right: 1px solid #e2e8f0;
  border-radius: 10px 0 0 10px;
  color: #475569;
  font-size: 12.5px;
  font-weight: 600;
  white-space: nowrap;
}
.search-scope:hover { color: #1B2A47; }
.search-scope select {
  appearance: none;
  -webkit-appearance: none;
  -moz-appearance: none;
  border: 0;
  background: transparent;
  font: inherit;
  color: inherit;
  padding: 9px 24px 9px 12px;
  cursor: pointer;
  outline: none;
  border-radius: 10px 0 0 10px;
}
/* Browsers paint their own focus ring/background on <select> even with
   outline:none on the base rule — that UA artifact is what made the seam
   look broken when focusing the scope instead of the input. Kill every
   variant; the wrapper's :focus-within ring is the only focus visual. */
.search-scope select:focus,
.search-scope select:focus-visible,
.search-scope select:active {
  outline: none;
  box-shadow: none;
  background: transparent;
}
.search-scope select::-moz-focus-inner { border: 0; }
/* While the control is focused (either half), tint the divider to match
   the ring instead of leaving a stray light-grey line mid-pill. */
.search-wrap:focus-within .search-scope { border-right-color: rgba(27, 42, 71, 0.25); }
.scope-chevron {
  width: 12px;
  height: 12px;
  position: absolute;
  right: 8px;
  pointer-events: none;
  color: #64748b;
}
.search-box {
  position: relative;
  flex: 1;
  display: flex;
  align-items: center;
}
.search-input.search-input-joined,
.search-input.search-input-joined:focus {
  border: 0;
  background: transparent;
  box-shadow: none;
  border-radius: 0 10px 10px 0;
}
.period-select { min-width: 118px; }
.search-icon {
  position: absolute;
  left: 12px;
  width: 16px; height: 16px;
  color: #94a3b8;
  pointer-events: none;
}
.search-input {
  width: 100%;
  padding: 9px 34px 9px 36px;
  border: 1px solid #e2e8f0;
  border-radius: 10px;
  font-size: 13px;
  background: #fbfaf7;
  outline: none;
  color: #0f172a;
  font-family: inherit;
  transition: border-color 0.15s, background 0.15s, box-shadow 0.15s;
}
.search-input:focus {
  border-color: #1B2A47;
  background: #fff;
  box-shadow: 0 0 0 4px rgba(27, 42, 71, 0.08);
}
.search-clear {
  position: absolute;
  right: 8px;
  border: none;
  background: transparent;
  cursor: pointer;
  color: #94a3b8;
  width: 24px; height: 24px;
  border-radius: 6px;
  display: inline-flex;
  align-items: center;
  justify-content: center;
  transition: all 0.12s;
}
.search-clear:hover { background: #f1f5f9; color: #475569; }
.search-clear svg { width: 14px; height: 14px; }

.chip-group { display: flex; gap: 4px; flex-wrap: wrap; }
.chip {
  border: 1px solid #e2e8f0;
  background: #fff;
  padding: 7px 14px;
  border-radius: 8px;
  font-size: 12px;
  color: #475569;
  cursor: pointer;
  font-weight: 500;
  transition: all 0.12s;
  font-family: inherit;
}
.chip:not(.chip-active):hover { border-color: #cbd5e1; background: #fbfaf7; }
.chip-active {
  background: #eef2f7;
  border-color: #cbd5e1;
  color: #1B2A47;
  font-weight: 600;
}
.chip-active-pending { background: #fef3c7; border-color: #fde68a; color: #b45309; }
.chip-active-success { background: #d1fae5; border-color: #a7f3d0; color: #047857; }
.chip-active-danger  { background: #fee2e2; border-color: #fecaca; color: #991b1b; }
.chip-active-neutral { background: #eef2f7; border-color: #cbd5e1; color: #1B2A47; }

/* ---------- Advanced filters ---------- */
.filters-advanced {
  padding: 14px;
  background: #fbfaf7;
  border: 1px solid #f1ede2;
  border-radius: 12px;
  display: flex;
  flex-direction: column;
  gap: 14px;
}
.advanced-grid {
  display: grid;
  grid-template-columns: repeat(auto-fit, minmax(160px, 1fr));
  gap: 12px;
}
.field { display: flex; flex-direction: column; gap: 4px; }
.field label {
  font-size: 11px;
  font-weight: 600;
  color: #64748b;
  letter-spacing: 0.02em;
  text-transform: uppercase;
}
.input-wrap {
  position: relative;
  display: flex;
  align-items: center;
}
.input-prefix {
  position: absolute;
  left: 12px;
  color: #94a3b8;
  font-size: 13px;
  pointer-events: none;
}
.input-wrap input {
  width: 100%;
  padding: 8px 12px 8px 24px;
  border: 1px solid #e2e8f0;
  border-radius: 10px;
  font-size: 13px;
  background: #fff;
  color: #0f172a;
  font-family: inherit;
  outline: none;
  transition: border-color 0.15s, box-shadow 0.15s;
}
.input-wrap input:focus {
  border-color: #1B2A47;
  box-shadow: 0 0 0 4px rgba(27, 42, 71, 0.08);
}

.select-wrap { position: relative; display: flex; align-items: center; }
.select-wrap select {
  appearance: none;
  -webkit-appearance: none;
  width: 100%;
  padding: 8px 32px 8px 12px;
  border: 1px solid #e2e8f0;
  border-radius: 10px;
  font-size: 13px;
  background: #fff;
  color: #0f172a;
  font-family: inherit;
  outline: none;
  cursor: pointer;
  transition: border-color 0.15s, box-shadow 0.15s;
}
.select-wrap select:focus {
  border-color: #1B2A47;
  box-shadow: 0 0 0 4px rgba(27, 42, 71, 0.08);
}
.select-chevron {
  position: absolute;
  right: 10px;
  width: 14px; height: 14px;
  color: #94a3b8;
  pointer-events: none;
}

.date-input {
  width: 100%;
  padding: 8px 12px;
  border: 1px solid #e2e8f0;
  border-radius: 10px;
  font-size: 13px;
  background: #fff;
  color: #0f172a;
  font-family: inherit;
  outline: none;
  transition: border-color 0.15s, box-shadow 0.15s;
}
.date-input:focus {
  border-color: #1B2A47;
  box-shadow: 0 0 0 4px rgba(27, 42, 71, 0.08);
}

.advanced-foot {
  display: flex;
  justify-content: flex-end;
  padding-top: 4px;
  border-top: 1px solid #f1ede2;
}

.collapse-enter-active, .collapse-leave-active { transition: opacity 0.2s ease, max-height 0.28s ease; overflow: hidden; }
.collapse-enter-from, .collapse-leave-to { opacity: 0; max-height: 0; }
.collapse-enter-to, .collapse-leave-from { opacity: 1; max-height: 600px; }

/* ==================== Loading + empty ==================== */
.pi-loading {
  padding: 60px 20px;
  display: flex;
  flex-direction: column;
  align-items: center;
  gap: 14px;
  flex: 1;
  justify-content: center;
}
.loading-spinner {
  width: 40px; height: 40px;
  border-radius: 999px;
  border: 3px solid #e2e8f0;
  border-top-color: #1B2A47;
  animation: spin 0.9s linear infinite;
}
@keyframes spin { to { transform: rotate(360deg); } }
.loading-label { font-size: 13px; color: #64748b; }

.empty-hero {
  background: #fff;
  border: 1px dashed #cbd5e1;
  border-radius: 16px;
  padding: 60px 24px;
  display: flex;
  flex-direction: column;
  align-items: center;
  gap: 8px;
  text-align: center;
  flex: 1;
  justify-content: center;
}
.empty-hero-icon {
  width: 56px; height: 56px;
  border-radius: 14px;
  background: #eef2f7;
  color: #1B2A47;
  display: flex; align-items: center; justify-content: center;
  margin-bottom: 4px;
}
.empty-hero-icon svg { width: 28px; height: 28px; }
.empty-hero-title { font-size: 15px; font-weight: 700; color: #1b2a47; }
.empty-hero-sub { font-size: 13px; color: #64748b; }

/* ==================== Body ==================== */
.pi-body {
  display: flex;
  flex-direction: column;
  gap: 14px;
  flex: 1;
  min-height: 0;
}
.pi-grid {
  display: grid;
  grid-template-columns: 1fr;
  gap: 12px;
}
.pagination-wrap {
  margin-top: auto;
  padding-top: 8px;
}

/* ==================== Toast ==================== */
.pi-toast {
  position: fixed;
  top: 24px; right: 24px;
  z-index: 60;
  background: #1b2a47; color: #fff;
  padding: 12px 18px;
  border-radius: 12px;
  font-size: 13px; font-weight: 500;
  display: inline-flex; align-items: center; gap: 10px;
  box-shadow: 0 12px 30px -10px rgba(27, 42, 71, 0.4);
}
.pi-toast .dot { width: 8px; height: 8px; border-radius: 999px; background: #94a3b8; }
.pi-toast.is-success { background: #059669; }
.pi-toast.is-success .dot { background: #a7f3d0; }
.pi-toast.is-error { background: #dc2626; }
.pi-toast.is-error .dot { background: #fecaca; }

.toast-enter-active, .toast-leave-active { transition: all 0.24s cubic-bezier(0.4, 0, 0.2, 1); }
.toast-enter-from, .toast-leave-to { transform: translateY(-12px); opacity: 0; }

/* ==================== Upload modal ==================== */
.modal-backdrop {
  position: fixed;
  inset: 0;
  background: rgba(15, 23, 42, 0.45);
  backdrop-filter: blur(6px);
  -webkit-backdrop-filter: blur(6px);
  z-index: 100;
  display: flex;
  align-items: center;
  justify-content: center;
  padding: 20px;
}
.modal-card {
  background: #fff;
  border-radius: 18px;
  width: 100%;
  max-width: 520px;
  max-height: calc(100vh - 40px);
  overflow: hidden;
  display: flex;
  flex-direction: column;
  box-shadow: 0 24px 60px -20px rgba(15, 23, 42, 0.35);
  border: 1px solid #ede9df;
}
.modal-head {
  display: flex;
  align-items: flex-start;
  justify-content: space-between;
  gap: 12px;
  padding: 20px 22px 14px;
  border-bottom: 1px solid #f1ede2;
}
.modal-eyebrow {
  font-size: 10px;
  letter-spacing: 0.12em;
  text-transform: uppercase;
  color: #8a8371;
  font-weight: 700;
}
.modal-title {
  font-size: 18px;
  font-weight: 700;
  color: #1b2a47;
  margin: 2px 0 4px;
  letter-spacing: -0.01em;
}
.modal-sub { font-size: 12.5px; color: #64748b; }
.modal-close {
  border: none;
  background: transparent;
  cursor: pointer;
  color: #94a3b8;
  width: 30px; height: 30px;
  border-radius: 8px;
  display: inline-flex; align-items: center; justify-content: center;
  transition: all 0.12s;
  flex-shrink: 0;
}
.modal-close:hover { background: #f1f5f9; color: #1b2a47; }
.modal-close svg { width: 16px; height: 16px; }
.modal-body {
  padding: 20px 22px;
  overflow-y: auto;
  display: flex;
  flex-direction: column;
  gap: 14px;
}
.modal-foot {
  display: flex;
  justify-content: flex-end;
  gap: 8px;
  padding: 14px 22px 18px;
  border-top: 1px solid #f1ede2;
}

.drop-zone {
  border: 2px dashed #cbd5e1;
  border-radius: 14px;
  padding: 32px 24px;
  text-align: center;
  cursor: pointer;
  transition: all 0.15s;
  display: flex;
  flex-direction: column;
  align-items: center;
  gap: 10px;
  background: #fbfaf7;
}
.drop-zone:hover { border-color: #94a3b8; background: #f4f0e5; }
.drop-zone.is-dragging { border-color: #1B2A47; background: #eef2f7; }
.drop-zone.has-file { border-color: #10b981; background: #ecfdf5; }
.drop-icon {
  width: 44px; height: 44px;
  border-radius: 12px;
  background: #fff;
  color: #64748b;
  display: flex; align-items: center; justify-content: center;
  border: 1px solid #e2e8f0;
}
.drop-icon svg { width: 22px; height: 22px; }
.drop-zone.has-file .drop-icon { color: #059669; border-color: #a7f3d0; }
.drop-title { font-size: 13px; font-weight: 600; color: #1b2a47; }
.drop-sub { font-size: 11.5px; color: #94a3b8; margin-top: 2px; }
.drop-file .file-name { font-size: 13px; font-weight: 600; color: #047857; word-break: break-all; }
.drop-file .file-size { font-size: 11.5px; color: #059669; margin-top: 2px; }
.hidden-input { display: none; }

.modal-fade-enter-active, .modal-fade-leave-active { transition: opacity 0.22s ease; }
.modal-fade-enter-from, .modal-fade-leave-to { opacity: 0; }
.modal-fade-enter-active .modal-card, .modal-fade-leave-active .modal-card {
  transition: transform 0.22s cubic-bezier(0.4, 0, 0.2, 1);
}
.modal-fade-enter-from .modal-card { transform: translateY(-8px) scale(0.98); }
</style>
